import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { COLORS } from '../../Styles/Colors';

/**
 * Shared pieces for pages that list ABATIX and Non-ABATIX rows together
 * (Inventory, View by Bin, Transactions): the Type badge, the type filter,
 * and the `?type=` URL parameter that keeps the filter on refresh / links.
 */

export type ItemType = 'abatix' | 'non-abatix';
export type ItemTypeFilter = 'all' | ItemType;

export const ITEM_TYPE_LABEL: Record<ItemType | 'asset', string> = {
  abatix: 'ABATIX',
  'non-abatix': 'Non-ABATIX',
  asset: 'Asset',
};

// Brand colours only (teal + gray): ABATIX is solid teal, Non-ABATIX is a teal
// outline, Assets are neutral gray — distinct at a glance without a second hue.
const CHIP_STYLE: Record<ItemType | 'asset', { color: string; background: string; border: string }> = {
  abatix: { color: COLORS.white, background: COLORS.teal, border: COLORS.teal },
  'non-abatix': { color: COLORS.teal, background: COLORS.white, border: COLORS.teal },
  asset: { color: COLORS.gray, background: '#f1f3f5', border: '#d5d9de' },
};

export const ItemTypeChip: React.FC<{ type: ItemType | 'asset' }> = ({ type }) => {
  const style = CHIP_STYLE[type] ?? CHIP_STYLE.asset;
  return (
    <span
      title={type === 'asset' ? 'Asset' : `${ITEM_TYPE_LABEL[type]} item`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        padding: '0 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        color: style.color,
        backgroundColor: style.background,
        border: `1px solid ${style.border}`,
      }}
    >
      {ITEM_TYPE_LABEL[type] ?? type}
    </span>
  );
};

export const parseItemTypeFilter = (raw: string | null | undefined): ItemTypeFilter =>
  raw === 'abatix' || raw === 'non-abatix' ? raw : 'all';

/** Reads/writes `?type=abatix|non-abatix`. "All" removes the parameter. */
export const useItemTypeParam = (): [ItemTypeFilter, (next: ItemTypeFilter) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = parseItemTypeFilter(searchParams.get('type'));

  const setValue = useCallback((next: ItemTypeFilter) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'all') params.delete('type');
      else params.set('type', next);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  return [value, setValue];
};

export const toggleGroupSx = {
  height: 40,
  flexShrink: 0,
  '& .MuiToggleButton-root': {
    px: 1.5,
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
    color: COLORS.gray,
    borderColor: `${COLORS.lightGray}55`,
    '&:hover': { backgroundColor: `${COLORS.teal}10` },
  },
  '& .MuiToggleButton-root.Mui-selected': {
    backgroundColor: COLORS.teal,
    color: COLORS.white,
    '&:hover': { backgroundColor: COLORS.teal },
  },
} as const;

interface ItemTypeToggleProps {
  value: ItemTypeFilter;
  onChange: (next: ItemTypeFilter) => void;
  disabled?: boolean;
}

/** All / ABATIX / Non-ABATIX segmented filter, styled like the View by Bin filter. */
export const ItemTypeToggle: React.FC<ItemTypeToggleProps> = ({ value, onChange, disabled }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value}
    disabled={disabled}
    aria-label="Filter by item type"
    onChange={(_e, next: ItemTypeFilter | null) => {
      if (next) onChange(next);
    }}
    sx={toggleGroupSx}
  >
    <ToggleButton value="all">All</ToggleButton>
    <ToggleButton value="abatix">ABATIX</ToggleButton>
    <ToggleButton value="non-abatix">Non-ABATIX</ToggleButton>
  </ToggleButtonGroup>
);
